import { ResourceDetailTabProps } from '@ciandtspike.io/plugin-ciandtspike-common';
import {
  Card,
  EmptyState,
  StatusBadge,
  StatusBadgeColors,
} from '@ciandtspike.io/plugin-ui';
import { Link } from '@backstage/core-components';
import { Typography, makeStyles, useTheme } from '@material-ui/core';

interface EnvLike {
  name: string;
  edge?: { hostname?: string; certificateStatus?: string };
  serviceUri?: string;
  imageRef?: string;
  artifactRepository?: string;
}

const useStyles = makeStyles(theme => ({
  grid: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  envName: {
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    margin: 0,
  },
  note: {
    color: theme.palette.text.secondary,
  },
  imageRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  imageLabel: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  imageValue: {
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
}));

const buildPublicUrl = (env: EnvLike): string | undefined =>
  env.edge?.hostname ? `https://${env.edge.hostname}` : undefined;

// The platform does not consistently populate `edge.certificateStatus` (it is
// empty even for environments that have served HTTPS for weeks), so readiness
// keys on the public hostname, and a TLS note is shown only when the platform
// actually reports a status.
const certNote = (env: EnvLike): string => {
  const status = env.edge?.certificateStatus;
  if (!status) return '';
  return status === 'ACTIVE' ? 'TLS ativo' : 'provisionando certificado';
};

function useStatusColors() {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const ready: StatusBadgeColors = {
    bg: isDark ? '#0a2318' : '#f0fdf4',
    fg: isDark ? '#4ade80' : '#166534',
    border: isDark ? '#14532d' : 'rgba(22, 101, 52, 0.2)',
    dot: isDark ? '#4ade80' : '#166534',
  };
  const provisioning: StatusBadgeColors = {
    bg: isDark ? '#431407' : '#fffbeb',
    fg: isDark ? '#fde68a' : '#b45309',
    border: isDark ? '#78350f' : '#fde68a',
    dot: isDark ? '#facc15' : '#b45309',
    pulse: true,
  };
  return { ready, provisioning };
}

export const CloudRunTab = ({ detail }: ResourceDetailTabProps) => {
  const classes = useStyles();
  const colors = useStatusColors();
  const envs = (detail.environmentStatuses as EnvLike[] | undefined) ?? [];

  if (envs.length === 0) {
    return (
      <EmptyState
        title="Endpoints indisponíveis"
        description="Este recurso ainda não expôs endpoints por ambiente."
      />
    );
  }

  return (
    <div className={classes.grid} aria-label="Cloud Run">
      {envs.map(env => {
        const url = buildPublicUrl(env);
        const ready = Boolean(url);
        const note = certNote(env);
        const image = env.imageRef ?? env.artifactRepository;
        return (
          <Card key={env.name}>
            <div className={classes.header}>
              <Typography
                variant="subtitle1"
                component="h3"
                className={classes.envName}
              >
                {env.name}
              </Typography>
              <StatusBadge
                label={ready ? 'Disponível' : 'Provisionando'}
                colors={ready ? colors.ready : colors.provisioning}
              />
            </div>

            {url ? (
              <Typography variant="body2">
                <Link to={url} target="_blank" rel="noreferrer">
                  {url}
                </Link>
                {note ? <span className={classes.note}> · {note}</span> : null}
              </Typography>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Edge ainda provisionando o endpoint público.
              </Typography>
            )}

            {image && (
              <div className={classes.imageRow}>
                <Typography variant="caption" className={classes.imageLabel}>
                  Imagem
                </Typography>
                <Typography
                  variant="caption"
                  className={classes.imageValue}
                  title={image}
                >
                  {image}
                </Typography>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};
